import React, { Fragment, memo, useEffect, useRef, useState } from 'react'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import tw from 'twin.macro'
import { Formik, FormikProps } from 'formik'
import { BankInformationSchema } from './BankPaymentForm.validator'
import {
  BankInformationRequest,
  BankPaymentFormProps,
  BankPaymentFormValues,
} from './BankPaymentForm.interface'
import { BankPaymentAccountType } from './BankPaymentForm.constants'

import { Button } from 'src/components/shared/Button'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { BankInformationContent } from './BankInformationContent'
import { BankData, getPaymentMethodDetails } from 'src/utils/paymentMethod'
import { CustomerPaymentMethod } from 'src/interfaces/customer'
import { customerComplianceStepIcons } from 'src/constants/customer/icons'
import { CustomerResubmitInfoType } from 'src/constants/customer/enums'
import { getCustomerOnboardingPaymentInformation } from 'src/selectors/customer'
import { colors } from 'src/theme'

const initialValues: BankPaymentFormValues = {
  nameOnCard: '',
  cardNumber: '',
  expirationDate: '',
  zipCode: '',
  routingNumber: '',
  accountNumber: '',
  accountType: undefined,
  formType: undefined,
}

const CustomerBankPaymentFormComponent = ({
  showCurrentPaymentMethod,
  onSubmitBankInformation,
}: BankPaymentFormProps) => {
  const customerOnboardingPaymentInformation = useSelector(getCustomerOnboardingPaymentInformation)
  const formikRef = useRef<FormikProps<BankPaymentFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<BankData | null>(null)

  useEffect(() => {
    const processPaymentMethod = (method: CustomerPaymentMethod) => {
      if (showCurrentPaymentMethod) {
        const mapped = {
          type: method.paymentMethodType as BankData['type'],
          accountLastDigits: method.bankAccountNumberLast4 || '',
          routingLastDigits: method.bankRoutingNumberLast4 || '',
        }
        setCurrentPaymentMethod(mapped)
      }
    }

    if (customerOnboardingPaymentInformation) {
      processPaymentMethod(customerOnboardingPaymentInformation)
    }
  }, [showCurrentPaymentMethod, customerOnboardingPaymentInformation])

  const getInitialValues = (): BankPaymentFormValues => {
    const baseValues = { ...initialValues }

    if (showCurrentPaymentMethod && customerOnboardingPaymentInformation?.bankAccountType) {
      const accountType =
        customerOnboardingPaymentInformation.bankAccountType.toLowerCase() === 'savings'
          ? BankPaymentAccountType.SAVINGS
          : BankPaymentAccountType.CHECKING
      baseValues.accountType = accountType
    }

    return baseValues
  }

  const handleFormSubmit = async (values: BankPaymentFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const requestBody: BankInformationRequest = {
        routingNumber: values.routingNumber || '',
        accountNumber: values.accountNumber || '',
        accountType: values.accountType || '',
      }

      await onSubmitBankInformation(requestBody)

      formikRef.current?.resetForm()
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      enableReinitialize
      onSubmit={handleFormSubmit}
      validationSchema={BankInformationSchema}
      innerRef={formikRef}
    >
      {({ handleSubmit, handleChange }) => {
        return (
          <div css={tw`w-[318px] bg-white rounded-lg flex flex-col justify-between`}>
            <div>
              <div>
                <div css={tw`px-4 pt-4 py-0`}>
                  {customerComplianceStepIcons[CustomerResubmitInfoType.PaymentMethod]}
                  <Typography variant="h4" css={tw`mt-0 mb-2`} style={{ fontSize: 16 }}>
                    {showCurrentPaymentMethod
                      ? 'Update Your Settlement Method'
                      : 'Add Settlement Method'}
                  </Typography>
                  <div css={tw`w-full h-[1px] bg-[#EDEDED]`} />
                  <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-4 mt-2`}>
                    To receive payouts and process transactions, you'll need to add a valid
                    settlement method.
                  </Typography>
                </div>
              </div>

              <div css={tw`mt-0 px-4`}>
                <div
                  css={tw`mb-4 p-3 flex flex-row items-center rounded-2xl`}
                  style={{ backgroundColor: colors.PRIMARY_PURPLE_100 }}
                >
                  <div
                    css={tw`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mr-3`}
                    style={{ backgroundColor: colors.PRIMARY_PURPLE_500 }}
                  >
                    <Typography css={tw`text-white text-xs font-bold`}>i</Typography>
                  </div>
                  <div css={tw`flex flex-col`}>
                    <Typography
                      css={tw`text-sm`}
                      style={{ color: colors.PRIMARY_PURPLE_500, fontSize: '14px' }}
                    >
                      Business accounts can only use a Business Bank Account. Contact support if you
                      need help.
                    </Typography>
                  </div>
                </div>

                {showCurrentPaymentMethod && currentPaymentMethod && (
                  <Fragment>
                    <div css={tw`mt-4 px-0`}>
                      <Typography variant="h4" css={tw`mb-2 text-sm font-semibold`}>
                        Current Settlement Method
                      </Typography>
                      <div css={tw`w-full h-[1px] bg-[#EDEDED] mb-4`} />
                    </div>
                    <div
                      css={tw`mb-4 p-4 rounded-lg bg-white border border-solid border-[#EDEDED]`}
                    >
                      <Typography css={tw`text-gray-900 text-sm`} variant="h4">
                        Bank Information
                      </Typography>
                      <Typography
                        color={colors.SHADES_GREY_500}
                        css={tw`mt-0.5 capitalize text-sm`}
                      >
                        {getPaymentMethodDetails(currentPaymentMethod)}
                      </Typography>
                    </div>
                  </Fragment>
                )}

                <div css={tw`relative overflow-hidden`}>
                  <Typography variant="h4" css={tw`mt-0 mb-2 text-sm font-semibold`}>
                    Enter Bank Information
                  </Typography>
                  <div css={tw`w-full h-[1px] bg-[#EDEDED] mb-4`} />
                  <BankInformationContent
                    handleChange={handleChange}
                    showCurrentPaymentMethod={showCurrentPaymentMethod}
                    currentPaymentMethod={customerOnboardingPaymentInformation}
                  />
                  <Button
                    data-testid="customer-account-onboarding-bank-payment-submit-btn"
                    className={tw`w-full mt-3 mb-3 py-2 px-8`}
                    size="small"
                    loading={isLoading}
                    onClick={() => handleSubmit()}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
            <FormErrorMessage css={tw`mb-4`} error={error} />
          </div>
        )
      }}
    </Formik>
  )
}

export const CustomerBankPaymentForm = memo(CustomerBankPaymentFormComponent)
